import type { FC, ReactNode } from 'react';
import styled from 'styled-components';
import {
  backdropLayer,
  gateScreenLayout,
  type GateBackdrop,
} from '~/components/gateBackdrop';
import { CAMO_GATE_BACKDROP } from '~/common/constants/gateBackdrop';
import { PageCountdown } from '~/components/countdown/PageCountdown';
import type { GatedCampaign } from '~/common/constants/campaigns';
import { fontFamilies } from '~/theme/typography';

interface CampaignScreenProps {
  title: string;
  countdownTitle: string | null;
  countdownAt: number | null;
  /** Defaults to the camo; `/signup` and the EU launch pass their own. */
  backdrop?: GateBackdrop;
  /** A `GatedCampaign`'s art, rendered as images above and below the card. */
  artwork?: GatedCampaign['artwork'];
  children: ReactNode;
}

/**
 * A campaign's full-viewport screen: camo, the countdown at the top, and a pop-up-style
 * card below it.
 *
 * **Every caller must look identical**, and they are not all the same campaign — the
 * private-sale gates and ad page share it with `/signup`, which collects for a different
 * mailing list. So a tweak made while looking at the private-sale card silently changes
 * `/signup` too. `grep CampaignScreen` for the current callers. Named `CampaignScreen`,
 * not `PrivateSaleScreen`, since BPES-246 for that reason.
 *
 * `title` is a prop because of that split: a gated campaign passes its `getCampaignCopy`
 * title, `/signup` passes whatever `SIGNUP_TITLE` holds this month.
 *
 * A shell, not a card component: the layout below is the fragile part, and the form
 * bodies differ too much to share, so they arrive as `children`. Field styles are in
 * `~/components/cardForm`. See `specs/private-access-sale.md`.
 *
 * **It pulls `react-countdown` (~4.3KB gzip) into every route that renders it**, whether
 * or not a countdown is configured. That was accepted for the private-sale routes, which
 * only take traffic during a campaign. It is a weaker trade on `/signup`, which is in the
 * main nav and so ships the library year-round for a timer that is usually off — a known
 * cost, not a settled one. Hoisting `PageCountdown` out to the routes would undo the
 * layout coupling this shell exists to own, so the fix, if it is worth one, is lazier
 * loading rather than a different owner.
 */
export const CampaignScreen: FC<CampaignScreenProps> = ({
  title,
  countdownTitle,
  countdownAt,
  backdrop = CAMO_GATE_BACKDROP,
  artwork,
  children,
}) => {
  // Matches `PageCountdown`'s own render condition: the header clearance below exists
  // for the countdown, so an unconfigured countdown must not leave a gap behind.
  const hasCountdown = Boolean(countdownTitle) || countdownAt !== null;

  return (
    <Container $hasCountdown={hasCountdown} $backdrop={backdrop}>
      <PageCountdown title={countdownTitle} at={countdownAt} />

      <CardWrap>
        {artwork && <Artwork src={artwork.above.src} alt={artwork.above.alt} />}
        <Card>
          <Title>{title}</Title>
          {children}
        </Card>
        {artwork && (
          <Artwork
            src={artwork.below.src}
            alt={artwork.below.alt}
            loading="lazy"
          />
        )}
      </CardWrap>
    </Container>
  );
};

// `justify-content: flex-start` overrides `gateScreenLayout`'s centring so the in-flow
// countdown (at or below 1280px) stays at the top of the page instead of being dragged
// down beside the card; `CardWrap` absorbs the slack. Getting this wrong is the one
// layout bug this screen has already shipped — the reasoning is in
// `specs/private-access-sale.md`, read it before touching these three declarations.
//
// The header clearance lives here, not in `PageCountdown`, because the product grids
// get the same offset from `PageWrapper` while `gateScreenLayout` has no vertical
// padding.
const Container = styled.div<{
  $hasCountdown: boolean;
  $backdrop: GateBackdrop;
}>`
  ${gateScreenLayout}
  ${({ $backdrop }) => backdropLayer($backdrop)}
  justify-content: flex-start;

  @media ${({ theme }) => theme.breakpoints.lg} {
    padding-top: ${({ $hasCountdown }) => ($hasCountdown ? '4.5rem' : '0')};
  }
`;

// `flex-shrink: 0` so a card taller than the leftover space grows the page and scrolls
// rather than being squeezed. A column so the optional artwork stacks around the card.
const CardWrap = styled.div`
  flex: 1 0 auto;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 24px;
  padding: 24px 0;
`;

// Intrinsic ratio kept by `height: auto`; 70% of the card's width (design request:
// 30% smaller than the card) so the art never outgrows the form on desktop and never
// clips on a phone. The art below the card is
// under the fold on a phone, hence the lazy hint; the one above is left eager (likely
// LCP). No `fetchpriority` — this React's typings predate it.
const Artwork = styled.img`
  display: block;
  width: 70%;
  max-width: 420px;
  height: auto;
`;

// The homepage pop-up's panel. No `backdrop-filter`: the card is 80% opaque over a 60%
// scrim, so the artwork is ~8% of each pixel beneath it — the blur is invisible and
// costs a re-rasterised backdrop region on the LCP path.
const Card = styled.div`
  width: 100%;
  max-width: 600px;
  background: ${({ theme }) => theme.colors.modalBackgroundBlur};
  border-radius: 16px;
  box-shadow: ${({ theme }) => theme.shadows.modal};
  padding: 40px;

  @media ${({ theme }) => theme.breakpoints.sm} {
    padding: 20px 16px;
  }
`;

const Title = styled.h1`
  color: ${({ theme }) => theme.colors.white};
  text-align: center;
  font-family: ${fontFamilies.PantonRustHeavy};
  font-size: 40px;
  font-weight: 100;
  line-height: normal;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  /* Env titles may carry a newline (withLineBreaks); honour it, collapse the rest. */
  white-space: pre-line;
  margin: 0 0 8px 0;

  @media ${({ theme }) => theme.breakpoints.sm} {
    font-size: 24px;
  }
`;
